import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { workspacesApi, type AttributionData } from '../../services/api'
import { Card, CardList, EmptyValue, ErrorState, Inline, Metric, SegmentedControl, TileGrid } from '../../ds'
import { ATTRIBUTION_WINDOWS, WINDOW_WORDS, type AttributionWindow } from './ledgerLines'

/**
 * Ledger's attribution strip (MAR-309) — the v6 prototype's value tiles and its
 * `24h · 7d · 30d · 365d` window, read from `GET /workspaces/{id}/attribution?period=`
 * (docs/reference/api/business-entities.md, "Attribution summary").
 *
 * It replaces Today's stat tiles, but deliberately not all of them: Today counted decisions,
 * "handled" and approval in the browser from the newest 100 activity rows, which is the defect
 * MAR-246 kept off Ledger. Every figure here is the server's own period read.
 *
 * ⚠️ It reads `byCurrency`, NEVER `summary.revenue[period].total` — that total adds currencies
 * together and is kept only for an old screen's shape.
 *
 * ⚠️ All four tiles describe ONE currency — the headline — so they never read as "10 orders worth
 * €400" when eight of the ten were in GBP:
 *  · the headline is the largest KNOWN currency, in that currency. Only when the server's `unknown`
 *    bucket (rows that stored no currency) is all there is does it become the headline, as a plain
 *    number;
 *  · other known currencies are named as "+ N other currencies". `unknown` is never counted as
 *    another currency — that would invent one — and its revenue is never added to any; when it
 *    sits beside a known headline it is named as "N orders not shown (no currency recorded)" — "at least N" on a truncated read;
 *  · "Orders she can claim" is the headline currency's count, and the average is its revenue over
 *    that count (a known currency only). Whenever the strip leaves rows out, both say "{CUR} only".
 *
 * ⚠️ Three things that must never read as zero, because zero is a claim:
 *  · a FAILED read is an error with a retry, never £0;
 *  · a read still in flight is a quiet placeholder, never £0;
 *  · `truncated: true` (the partition held more than the server's row cap, so the OLDEST orders
 *    were not read) makes each count a floor — "at least".
 *  A store with nothing attributed, read successfully, is the one honest zero.
 *
 * ⚠️ State is scoped at the READ, as everything on Ledger is (docs/work/app-ia-v5.md §4): the
 * stored result names the workspace and the window it answers, and is rendered only when both
 * match what is on screen. A workspace switch therefore never paints workspace A's revenue
 * under B, not even for one render; and a response that is not the newest read writes nothing.
 *
 * "Share of revenue" (MAR-392) is the headline currency's attributed revenue over the store's own
 * revenue in THAT currency for the same window (`storeRevenue`, counted as attribution counts:
 * every order placed, gross, cancellations included). It is a share or it is "—" with the reason
 * on the line under it — never a share computed from a partial sum: a truncated store total, a
 * truncated attribution read (a floor), a failed store read, a headline with no currency, no
 * store orders in that currency, or an attributed figure larger than the store's (orders not yet
 * synced) each show "—". Currencies are never mixed: the share is one currency's, and says which
 * whenever either side holds another.
 *
 * Omitted from the design, each for want of a source: "of N all time", and "How she assigns
 * credit →" (no page). The four tiles fill the width (`TileGrid` is auto-fit).
 *
 * At an honest zero — a successful, untruncated read with nothing attributed — the tiles
 * fold into ONE bordered row (MAR-316 finding 06; the v6 design, MAR-402): "**Nothing attributed
 * to her** in the last 24 hours — no orders she can claim.", with the window control right-aligned
 * on the same line (named "Attributed over", no visible caption), so the decisions move up the
 * page and the merchant can still widen the window. Every word of it is true only in that state:
 * a truncated read is a floor (it keeps the tiles), and nothing attributed means no order to claim.
 * With tiles, the same control sits right-aligned ABOVE them (no caption either), so it stays at
 * the strip's top right in both states (MAR-412).
 *
 * The window is the Ledger's (it heads the list too): pass `period` and `onPeriodChange` to
 * control it; without them the strip keeps its own.
 */

type CurrencyRow = AttributionData['byCurrency'][number]

type StripState = {
  workspaceId: string
  period: AttributionWindow
  status: 'ready' | 'error'
  data: AttributionData | null
}

/** The label the server gives a row that stored no currency. No currency is invented for it. */
const UNKNOWN_CURRENCY = 'unknown'

/**
 * A revenue figure in its own currency, no decimals. `unknown` — and any code `Intl` refuses —
 * is a plain number: rendering it as £ would invent the very currency the row did not store.
 */
function fmtIn(amount: number, currency: string): string {
  if (currency && currency !== UNKNOWN_CURRENCY) {
    try {
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount)
    } catch {
      // An unrecognised code: fall through to the plain number.
    }
  }
  return new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 }).format(amount)
}

/** One value tile: the design system's Metric — label, value, and the line under it. */
function Tile({ label, value, sub, floor }: { label: string; value: string; sub: string; floor?: boolean }) {
  return <Metric label={label} value={floor ? `at least ${value}` : value} period={sub} />
}

type StoreRevenue = NonNullable<AttributionData['storeRevenue']>

/**
 * The Share-of-revenue tile's value and its line: a share of ONE known currency, or "—" and why.
 * `leavesOut` is the strip's own "the headline is not everything" — the share then names its currency.
 */
function shareOf(
  data: AttributionData,
  top: CurrencyRow | undefined,
  words: string,
  leavesOut: boolean
): { value: string; sub: string } {
  const none = (sub: string) => ({ value: '—', sub })
  const store: StoreRevenue | null | undefined = data.storeRevenue
  if (!store || data.storeRevenueUnavailable || !Array.isArray(store.byCurrency)) return none('store total unavailable')
  if (store.truncated !== false) {
    return none(store.stoppedBy === 'deadline' ? 'store total took too long to read' : 'too many store orders to total')
  }
  // Her side is a floor: a share of a floor is not a share.
  if (data.truncated === true) return none('not all her orders were read')
  if (!top || top.currency === UNKNOWN_CURRENCY) return none('no currency recorded')
  const storeRow = store.byCurrency.find((r) => r.currency === top.currency)
  if (!storeRow || !(storeRow.revenue > 0)) return none(`no ${top.currency} store orders recorded in ${words}`)
  // Hers can only exceed the store's while the store's orders are still syncing.
  if (top.revenue > storeRow.revenue) return none('store orders still syncing')
  const share = top.revenue / storeRow.revenue
  // Rounding never claims an extreme the figures do not reach: 0.4% is "<1%", 99.6% is ">99%"
  // (only an exact match is 100%).
  let value = `${Math.round(share * 100)}%`
  if (share > 0 && share < 0.01) value = '<1%'
  else if (share >= 0.99 && share < 1) value = '>99%'
  const storeOthers = store.byCurrency.some((r) => r.currency !== top.currency && (r.count > 0 || r.revenue > 0))
  const named = leavesOut || storeOthers ? `${top.currency} ` : ''
  return { value, sub: `of ${named}store revenue in ${words}` }
}

/** Nothing at all attributed: every currency row (if any) counts no order and no revenue. */
function nothingAttributed(data: AttributionData): boolean {
  return data.truncated !== true && data.byCurrency.every((r) => !(r.count > 0) && !(r.revenue > 0))
}

export default function AttributionStrip({
  workspaceId,
  period: controlledPeriod,
  onPeriodChange,
}: {
  workspaceId: string | undefined
  period?: AttributionWindow
  onPeriodChange?: (w: AttributionWindow) => void
}) {
  const [ownPeriod, setOwnPeriod] = useState<AttributionWindow>('24h')
  const period = controlledPeriod ?? ownPeriod
  const setPeriod = (w: AttributionWindow) => {
    if (controlledPeriod === undefined) setOwnPeriod(w)
    onPeriodChange?.(w)
  }
  const [state, setState] = useState<StripState | null>(null)
  const [reload, setReload] = useState(0)
  // Bumped by every read; a response that is not the newest read writes nothing.
  const seqRef = useRef(0)

  useEffect(() => {
    if (!workspaceId) return
    const seq = ++seqRef.current
    const ws = workspaceId
    const p = period
    workspacesApi
      .getAttribution(ws, p)
      .catch(() => null)
      .then((res) => {
        if (seq !== seqRef.current) return
        // A response without a `byCurrency` array is not one this strip can read — an error,
        // never zeros.
        if (res?.success && res.data && Array.isArray(res.data.byCurrency)) {
          setState({ workspaceId: ws, period: p, status: 'ready', data: res.data })
        } else {
          setState({ workspaceId: ws, period: p, status: 'error', data: null })
        }
      })
  }, [workspaceId, period, reload])

  // Scoped at the read: a result for another workspace or another window is not this one, and
  // reads as still loading.
  const view = state && workspaceId && state.workspaceId === workspaceId && state.period === period ? state : null
  const words = WINDOW_WORDS[period]

  const zero = !!(view && view.status === 'ready' && view.data && nothingAttributed(view.data))

  // ⚠️ The zero row and the tiles are different trees (the row is ONE bordered surface holding the
  // window; the tiles head it on a row of its own), so switching between them remounts the window control.
  // A window chosen from the keyboard can itself cause that switch (24h on the zero row → 7d reads
  // as loading tiles), and a remount drops focus to <body>. So the strip remembers whether focus
  // was in the control and, after a switch that lost it, puts it back on the checked window.
  const controlId = useId()
  const controlFocused = useRef(false)
  const controlFocus = {
    'data-attribution-window': controlId,
    onFocusCapture: () => {
      controlFocused.current = true
    },
    onBlurCapture: (e: React.FocusEvent<HTMLElement>) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) controlFocused.current = false
    },
  }
  useLayoutEffect(() => {
    if (!controlFocused.current) return
    const active = document.activeElement
    if (active && active !== document.body) return
    const radio = document.querySelector<HTMLElement>(
      `[data-attribution-window="${controlId}"] [role="radio"][aria-checked="true"]`
    )
    if (radio) radio.focus()
    else controlFocused.current = false
  }, [zero, controlId])

  const windowControl = (
    <SegmentedControl
      label="Attributed over"
      size="sm"
      options={[...ATTRIBUTION_WINDOWS]}
      value={period}
      onChange={(w) => setPeriod(w as AttributionWindow)}
    />
  )

  // The honest zero: one bordered row, the sentence and the window on one line (wrapping under
  // it when the row is narrow).
  if (zero) {
    return (
      <Card padding="sm" data-testid="attribution-zero-row">
        <Inline justify="between" {...controlFocus}>
          <p className="mv-prose t-small" data-testid="attribution-zero">
            <strong>Nothing attributed to her</strong>{' '}
            <EmptyValue>in {words} — no orders she can claim.</EmptyValue>
          </p>
          {windowControl}
        </Inline>
      </Card>
    )
  }

  let tiles: React.ReactNode
  if (!view) {
    tiles = (
      <>
        <Tile label="Attributed revenue" value="…" sub={`over ${words}`} />
        <Tile label="Share of revenue" value="…" sub={`of store revenue in ${words}`} />
        <Tile label="Orders she can claim" value="…" sub={`in ${words}`} />
        <Tile label="Average order" value="…" sub="her orders only" />
      </>
    )
  } else if (view.status === 'error' || !view.data) {
    tiles = (
      <ErrorState
        title="She couldn’t read attribution just now."
        // Cleared in the handler, not an effect: the retry reads as loading, not as the
        // failure it is replacing.
        onRetry={() => {
          setState(null)
          setReload((n) => n + 1)
        }}
      />
    )
  } else {
    const rows: CurrencyRow[] = view.data.byCurrency
    const floor = view.data.truncated === true
    const known = rows.filter((r) => r.currency !== UNKNOWN_CURRENCY)
    const noCurrency = rows.find((r) => r.currency === UNKNOWN_CURRENCY)
    // Largest first, per the contract — but sorted here too, so the headline cannot depend on it.
    // A known currency always beats the `unknown` bucket; that bucket heads only when it is alone.
    const top: CurrencyRow | undefined = [...known].sort((a, b) => b.revenue - a.revenue)[0] ?? noCurrency
    const topKnown = !!top && top.currency !== UNKNOWN_CURRENCY
    // Other KNOWN currencies. `unknown` is not "another currency".
    const others = topKnown ? known.length - 1 : 0
    // The no-currency orders are left out of a known headline, and said so.
    const hidden = topKnown && noCurrency ? noCurrency.count || 0 : 0
    const leavesOut = others > 0 || (topKnown && !!noCurrency)
    const only = leavesOut && top ? ` · ${top.currency} only` : ''
    const orders = top?.count || 0
    const avg = topKnown && top && top.count > 0 ? top : null

    const revenueSub = [
      others > 0 ? `+ ${others} other ${others === 1 ? 'currency' : 'currencies'}` : null,
      `over ${words}`,
      // Counted from the same read as the tiles, so a truncated read makes it a floor too.
      topKnown && noCurrency
        ? `${floor ? 'at least ' : ''}${hidden.toLocaleString('en-GB')} order${hidden === 1 ? '' : 's'} not shown (no currency recorded)`
        : null,
    ]
      .filter(Boolean)
      .join(' · ')
    const share = shareOf(view.data, top, words, leavesOut)
    const herOrders = `her ${floor && avg ? 'most recent ' : ''}${leavesOut && top ? `${top.currency} ` : ''}orders only`
    tiles = (
      <>
        <Tile
          label="Attributed revenue"
          // Nothing attributed: a real zero, with no currency symbol — a store with no
          // attributed orders has told us no currency, and this strip never invents one.
          value={top ? fmtIn(top.revenue, top.currency) : '0'}
          sub={revenueSub}
          floor={floor && !!top}
        />
        <Tile label="Share of revenue" value={share.value} sub={share.sub} />
        <Tile
          label="Orders she can claim"
          // The headline currency's orders — never a count across currencies under one figure.
          value={orders.toLocaleString('en-GB')}
          sub={`in ${words}${only}`}
          floor={floor && orders > 0}
        />
        <Tile
          label="Average order"
          // Only in a KNOWN currency: the headline's revenue over the headline's orders.
          value={avg ? fmtIn(avg.revenue / avg.count, avg.currency) : '—'}
          sub={herOrders}
        />
      </>
    )
  }

  // The window heads the tiles at the right, where the zero row holds it — so switching between
  // the two states never moves the control the merchant just used (MAR-412).
  return (
    <CardList>
      <Inline justify="end" {...controlFocus}>
        {windowControl}
      </Inline>
      <TileGrid aria-busy={!view} data-testid="attribution-tiles">
        {tiles}
      </TileGrid>
    </CardList>
  )
}
